import { AiFieldProvenance } from "src/app/core/services/ai-extraction-state.service";
import {
  ExtractionDocument,
  ExtractionItem,
} from "src/app/core/services/property-extraction.service";

/**
 * Do resultado revisto para o que o formulário do imóvel precisa.
 *
 * Só entram linhas aceites ou editadas. Os ids de catálogo já vêm resolvidos do
 * servidor, por isso aqui não há correspondência por texto. Funções puras.
 */

export interface AiFormPatchOwner {
  contactId: number | null;
  name: string;
  fiscalIdentification: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
}

export interface AiFormPatch {
  runId: number;
  /** Valores por nome de controlo do formulário. */
  details: any;
  address: any;
  areas: any;
  divisions: Array<{
    divisionId: number;
    count: number;
    area: number | null;
    individualAreas: number[];
  }>;
  nearby: Array<{ nearbyId: number; unitId: number; distance: number | null }>;
  characteristics: Array<{ groupId: number; characteristicId: number }>;
  owners: AiFormPatchOwner[];
  /** Chaves das linhas aplicadas, para o servidor registar a proveniência. */
  appliedKeys: string[];
  provenance: AiFieldProvenance[];
  /** Fotografias carregadas no painel, para a galeria do imóvel. */
  photoFiles: File[];
}

export interface AiLocationMatch {
  country?: { id: number; name: string } | null;
  region?: { id: number; name: string } | null;
  district?: { id: number; name: string } | null;
  county?: { id: number; name: string } | null;
  parish?: { id: number; name: string } | null;
}

const DATE_TYPES = ["date"];
const LOCATION_KEYS = ["districtName", "countyName", "parishName"];

export function isAccepted(item: { decision: string }): boolean {
  return item.decision === "ACCEPTED" || item.decision === "EDITED";
}

export function hasValue(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const text = value.trim();
    return text !== "" && text !== "---";
  }
  if (typeof value === "number") return Number.isFinite(value) && value !== 0;
  return true;
}

/** Valor final de uma linha de campo: o editado, ou o lido. */
export function fieldPatchValue(item: ExtractionItem): any {
  const final = item.finalValue;
  if (final && typeof final === "object" && "value" in final) return final.value;
  return item.payload?.patchValue ?? null;
}

/** Conteúdo final de um item (proprietário, divisão, …): o editado, ou o lido. */
export function itemPayload(item: ExtractionItem): any {
  const final = item.finalValue;
  if (final && typeof final === "object" && !("value" in final)) return final;
  return item.payload ?? {};
}

export function toDate(value: any): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const text = `${value ?? ""}`.trim();
  if (!text) return null;
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00` : text);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Valor no formato que o controlo do formulário espera. */
export function toControlValue(item: ExtractionItem, value: any): any {
  const type = `${item.payload?.type ?? "text"}`;
  if (value === null || value === undefined) return value;
  if (item.key === "yearOfConstruction") {
    const year = Number(value);
    return Number.isFinite(year) && year > 0 ? new Date(`${year}-01-01T00:00:00`) : null;
  }
  if (DATE_TYPES.indexOf(type) >= 0) return toDate(value);
  return value;
}

/**
 * Compara o valor atual de um controlo com o valor aplicado por IA, para saber
 * se o badge ainda faz sentido. Tolera datas, números em texto e ids em texto.
 */
export function sameControlValue(current: any, applied: any): boolean {
  if (!hasValue(current) && !hasValue(applied)) return false;
  if (current instanceof Date || applied instanceof Date) {
    const a = toDate(current);
    const b = toDate(applied);
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }
  if (typeof current === "boolean" || typeof applied === "boolean") {
    return !!current === !!applied;
  }
  const numericCurrent = Number(current);
  const numericApplied = Number(applied);
  if (
    `${current}`.trim() !== "" &&
    `${applied}`.trim() !== "" &&
    Number.isFinite(numericCurrent) &&
    Number.isFinite(numericApplied)
  ) {
    return Math.abs(numericCurrent - numericApplied) < 0.005;
  }
  // Uma data guardada como texto ISO.
  const dateCurrent = /^\d{4}-\d{2}-\d{2}/.test(`${current}`) ? toDate(current) : null;
  const dateApplied = /^\d{4}-\d{2}-\d{2}/.test(`${applied}`) ? toDate(applied) : null;
  if (dateCurrent && dateApplied) return sameControlValue(dateCurrent, dateApplied);
  return `${current}`.trim().toLowerCase() === `${applied}`.trim().toLowerCase();
}

function provenanceOf(
  item: ExtractionItem,
  tab: string,
  control: string,
  value: any,
  documents: Map<number, ExtractionDocument>,
): AiFieldProvenance {
  const document = item.documentId ? documents.get(item.documentId) : null;
  return {
    tab,
    control,
    value,
    documentName: document?.fileName ?? item.payload?.sourceFileName ?? null,
    page: item.page ?? null,
    confidence: item.confidence ?? null,
  };
}

export function buildFormPatch(
  runId: number,
  items: ExtractionItem[],
  documents: ExtractionDocument[],
  location: AiLocationMatch | null,
): AiFormPatch {
  const patch: AiFormPatch = {
    runId,
    details: {},
    address: {},
    areas: {},
    divisions: [],
    nearby: [],
    characteristics: [],
    owners: [],
    appliedKeys: [],
    provenance: [],
    photoFiles: [],
  };
  const documentsById = new Map<number, ExtractionDocument>();
  for (const document of documents ?? []) documentsById.set(document.id, document);

  const accepted = (items ?? []).filter(isAccepted);

  for (const item of accepted) {
    if (item.kind === "FIELD") {
      const form = item.payload?.form;
      if (!form?.tab || !form?.control) continue;
      const raw = fieldPatchValue(item);
      if (raw === null || raw === undefined || raw === "") continue;
      const value = toControlValue(item, raw);
      if (value === null || value === undefined) continue;

      const target =
        form.tab === "details"
          ? patch.details
          : form.tab === "address"
          ? patch.address
          : patch.areas;
      target[form.control] = value;

      if (LOCATION_KEYS.indexOf(item.key) >= 0) {
        const label = item.finalValue?.label ?? item.payload?.catalogLabel ?? item.value;
        if (label) patch.address[item.key] = label;
      }

      patch.appliedKeys.push(item.key);
      patch.provenance.push(
        provenanceOf(item, form.tab, form.control, value, documentsById),
      );
      continue;
    }

    const payload = itemPayload(item);

    if (item.kind === "DIVISION") {
      const divisionId = Number(payload?.divisionId ?? 0);
      const count = Math.floor(Number(payload?.count ?? 0));
      if (!divisionId || !Number.isFinite(count) || count <= 0) continue;
      patch.divisions.push({
        divisionId,
        count,
        area: Number.isFinite(Number(payload?.area)) ? Number(payload.area) : null,
        individualAreas: (Array.isArray(payload?.individualAreas)
          ? payload.individualAreas
          : []
        )
          .map((value: any) => Number(value))
          .filter((value: number) => Number.isFinite(value) && value > 0),
      });
      patch.appliedKeys.push(item.key);
      patch.provenance.push(
        provenanceOf(item, "areasNearby", `division${divisionId}`, count, documentsById),
      );
      continue;
    }

    if (item.kind === "PROXIMITY") {
      const nearbyId = Number(payload?.nearbyId ?? 0);
      const unitId = Number(payload?.unitId ?? 0);
      // Sem unidade a gravação do imóvel falha: fica de fora.
      if (!nearbyId || !unitId) continue;
      const distance = Number(payload?.distance);
      patch.nearby.push({
        nearbyId,
        unitId,
        distance: Number.isFinite(distance) && distance > 0 ? distance : null,
      });
      patch.appliedKeys.push(item.key);
      continue;
    }

    if (item.kind === "CHARACTERISTIC") {
      const groupId = Number(payload?.groupId ?? 0);
      const characteristicId = Number(payload?.characteristicId ?? 0);
      if (!groupId || !characteristicId) continue;
      patch.characteristics.push({ groupId, characteristicId });
      patch.appliedKeys.push(item.key);
      continue;
    }

    if (item.kind === "OWNER") {
      const name = `${payload?.fullName ?? ""}`.trim();
      const contactId = Number(payload?.selectedContactId ?? 0) || null;
      if (!name && !contactId) continue;
      const contact = (payload?.contactCandidates ?? []).find(
        (candidate: any) => Number(candidate?.id) === contactId,
      );
      patch.owners.push({
        contactId,
        name: name || `${contact?.name ?? ""}`.trim(),
        fiscalIdentification:
          payload?.fiscalIdentification ?? contact?.fiscalIdentification ?? null,
        email: payload?.email ?? contact?.email ?? null,
        phone: payload?.phone ?? contact?.phone ?? null,
        address: payload?.address ?? null,
      });
      patch.appliedKeys.push(item.key);
    }
  }

  // A morada precisa do país e da região do distrito escolhido.
  if (hasValue(patch.address.district) && location?.district?.id) {
    if (Number(patch.address.district) === Number(location.district.id)) {
      if (location.country?.id) patch.address.country = location.country.id;
      if (location.region?.id) patch.address.region = location.region.id;
    }
  }
  // Concelho e freguesia só fazem sentido dentro do nível acima.
  if (!hasValue(patch.address.district)) {
    delete patch.address.county;
    delete patch.address.countyName;
  }
  if (!hasValue(patch.address.county)) {
    delete patch.address.parish;
    delete patch.address.parishName;
  }

  return patch;
}
